/**
 * Pipeline factory and convenience functions
 */

import {
  TrackSessionProcessingPipeline,
  PipelineOptions,
  PipelineResult,
  FileInputMetadata,
} from './ProcessingPipeline'
import {
  DtoConversionStage,
  FileInputStage,
  TelemetryTrimmingStage,
  RawTelemetryProcessingStage,
  TelemetrySynchronizationStage,
  LapDetectionStage,
  LapProcessingStage,
} from './stages'
import { TrackSessionDto, LapDto, TrackDto } from '../../api/apiTypes'
import { Track } from '../../domain/track/Track'
import { TrackType } from '../../domain/track/TrackMetadata'
import { RawTelemetryData } from '../../domain/telemetry/raw/RawTelemetryData'
import { EMPTY_GUID } from '../../utils/guidUtils'

/**
 * Patch single-point finish lines that are lost during DTO-to-domain conversion.
 * The domain FinishLine model requires two points (startPoint/endPoint), so single-point
 * finish lines from predefined tracks are dropped during trackDtoToDomain. Re-apply them
 * from the original DTO coordinates so applyStartFinishForSession can use them.
 */
const patchSinglePointFinishLine = (
  result: PipelineResult,
  trackDto: TrackDto | null
): void => {
  if (
    trackDto != null &&
    trackDto.finishLine != null &&
    trackDto.finishLine.length >= 1 &&
    !result.trackSession.track.finishLine
  ) {
    result.trackSession.track.finishLine = trackDto.finishLine.map((c) => ({
      latitude: c.latitude,
      longitude: c.longitude,
    }))
  }
}

/**
 * Convert TrackDto (API format) to domain Track
 */
const trackDtoToDomain = (trackDto: TrackDto): Track => {
  // Convert finish line from Coordinate[] to FinishLine object
  const finishLine =
    trackDto.finishLine?.length >= 2
      ? {
          startPoint: {
            latitude: trackDto.finishLine[0].latitude,
            longitude: trackDto.finishLine[0].longitude,
          },
          endPoint: {
            latitude: trackDto.finishLine[1].latitude,
            longitude: trackDto.finishLine[1].longitude,
          },
        }
      : undefined

  return new Track(
    {
      trackId: trackDto.id,
      trackName: trackDto.name,
      location: { country: trackDto.countryCode ?? 'UN' },
      type: TrackType.Circuit,
      surfaces: [],
    },
    finishLine,
    trackDto.isUserSuggested ?? false
  )
}

/**
 * Create an unknown track domain object
 */
export const createUnknownTrack = (): Track => {
  return new Track(
    {
      trackId: EMPTY_GUID,
      trackName: 'Unknown',
      location: { country: 'UN' },
      type: TrackType.Unknown,
      surfaces: [],
    },
    undefined,
    false
  )
}

/**
 * Create a standard processing pipeline with all default stages for DTO input
 */
export const createStandardPipeline = (
  options?: PipelineOptions
): TrackSessionProcessingPipeline => {
  const pipeline = new TrackSessionProcessingPipeline(options)

  // Core stages - always needed
  pipeline.addStage(new DtoConversionStage())
  pipeline.addStage(new TelemetryTrimmingStage())
  pipeline.addStage(new RawTelemetryProcessingStage())
  pipeline.addStage(new TelemetrySynchronizationStage())

  // Lap stages - skip when applyStartFinishForSession() will redo this work
  if (!options?.skipLapStages) {
    pipeline.addStage(new LapDetectionStage())
    pipeline.addStage(new LapProcessingStage())
  }

  return pipeline
}

/**
 * Create a processing pipeline for file input (raw telemetry)
 *
 * @param rawTelemetry - The raw telemetry data from the device file
 * @param hasFinishLine - Whether the track has a finish line defined (skip lap stages if false)
 * @param options - Pipeline options
 */
export const createFilePipeline = (
  rawTelemetry: RawTelemetryData,
  hasFinishLine: boolean = true,
  options?: PipelineOptions
): TrackSessionProcessingPipeline => {
  const pipeline = new TrackSessionProcessingPipeline(options)

  // Core stages - always needed
  pipeline.addStage(new FileInputStage(rawTelemetry))
  pipeline.addStage(new TelemetryTrimmingStage())
  pipeline.addStage(new RawTelemetryProcessingStage())
  pipeline.addStage(new TelemetrySynchronizationStage())

  // Lap-related stages - only when finish line is defined
  if (hasFinishLine) {
    pipeline.addStage(new LapDetectionStage())
    pipeline.addStage(new LapProcessingStage())
  }

  return pipeline
}

/**
 * Process a track session DTO using the standard pipeline
 *
 * This is the main entry point for converting DTOs to domain objects
 *
 * @example
 * ```typescript
 * const result = await processTrackSessionDto(
 *   sessionDto,
 *   track,
 *   lapDtos,
 *   {
 *     calculateLeanAngle: true,
 *     calculateGForce: true,
 *     syncIntervalMs: 100,
 *     onProgress: (stage, progress) => {
 *       console.log(`${stage}: ${progress}%`)
 *     }
 *   }
 * )
 *
 * const trackSession = result.trackSession
 * console.log(`Processed ${trackSession.lapCount} laps`)
 * ```
 */
export const processTrackSessionDto = async (
  dto: TrackSessionDto,
  track: Track,
  lapDtos?: LapDto[],
  options?: PipelineOptions
): Promise<PipelineResult> => {
  const pipeline = createStandardPipeline(options)
  return pipeline.execute(dto, track, lapDtos)
}

/**
 * Process a track session DTO using TrackDto format (API format)
 *
 * This is a convenience function that accepts TrackDto instead of domain Track.
 * Use this when working with API responses directly.
 *
 * @example
 * ```typescript
 * const result = await processTrackSessionWithTrackDto(
 *   sessionDto,
 *   trackDto, // TrackDto from API
 *   lapDtos
 * )
 * const trackSession = result.trackSession
 * ```
 */
export const processTrackSessionWithTrackDto = async (
  dto: TrackSessionDto,
  trackDto: TrackDto,
  lapDtos?: LapDto[],
  options?: PipelineOptions
): Promise<PipelineResult> => {
  const track = trackDtoToDomain(trackDto)
  const result = await processTrackSessionDto(dto, track, lapDtos, options)

  patchSinglePointFinishLine(result, trackDto)

  return result
}

/**
 * Process raw telemetry from a file using the pipeline
 *
 * This is the main entry point for converting device files to domain objects.
 * Use this when loading data from device files (PzRacing, Racebox, etc.)
 *
 * @example
 * ```typescript
 * const result = await processRawTelemetryFile(
 *   rawTelemetry,
 *   track,
 *   {
 *     sessionId: generateUuid(),
 *     fileName: 'session.json',
 *     device: { name: 'Racebox', version: '1.0' },
 *     sessionFeatures: { hasVideo: false, hasLeanAngle: false },
 *   },
 *   {
 *     calculateLeanAngle: true,
 *     onProgress: (stage, progress) => console.log(`${stage}: ${progress}%`)
 *   }
 * )
 *
 * const trackSession = result.trackSession
 * console.log(`Processed ${trackSession.lapCount} laps`)
 * ```
 */
export const processRawTelemetryFile = async (
  rawTelemetry: RawTelemetryData,
  track: Track,
  fileMetadata: FileInputMetadata,
  options?: PipelineOptions
): Promise<PipelineResult> => {
  const hasFinishLine = !!track.finishLine
  const pipeline = createFilePipeline(rawTelemetry, hasFinishLine, options)
  return pipeline.executeFromFile(track, fileMetadata)
}

/**
 * Process raw telemetry from a file using TrackDto format (API format)
 *
 * This is a convenience function that accepts TrackDto instead of domain Track.
 *
 * @example
 * ```typescript
 * const result = await processRawTelemetryWithTrackDto(
 *   rawTelemetry,
 *   trackDto, // TrackDto from API, or null for unknown track
 *   { sessionId, fileName, device, sessionFeatures }
 * )
 * const trackSession = result.trackSession
 * ```
 */
export const processRawTelemetryWithTrackDto = async (
  rawTelemetry: RawTelemetryData,
  trackDto: TrackDto | null,
  fileMetadata: FileInputMetadata,
  options?: PipelineOptions
): Promise<PipelineResult> => {
  const track = trackDto ? trackDtoToDomain(trackDto) : createUnknownTrack()
  const result = await processRawTelemetryFile(
    rawTelemetry,
    track,
    fileMetadata,
    options
  )

  patchSinglePointFinishLine(result, trackDto)

  return result
}

/**
 * Create a custom pipeline with specific stages
 */
export const createCustomPipeline = (
  options?: PipelineOptions
): TrackSessionProcessingPipeline => {
  return new TrackSessionProcessingPipeline(options)
}

// Export all pipeline components
export * from './ProcessingPipeline'
export * from './stages'
